import { useState, useEffect, useReducer, useRef } from "react";
import axios from "../../../lib/http/axiosConfig";
import "./Mission.css";
import DroneDetailPanelControl from "./DroneControl";
import SchedulerPanel from "./SchedulerPanel";
import SectionPanel from "./SectionPanel";
import { useDispatch, useSelector } from "react-redux";
import {
  latchMissionResult,
  setControl,
  setInfoConnected,
  setMissionProgress,
  setMissionReturning,
  setSelectedDrone,
  setUploadStatus,
} from "../../../features/DroneSlice";
import { missionProgressLabel } from "../../../utils/missionProgress";
import {
  fetchWaypoints,
  setRouteConfirmed,
  setWaypoints,
} from "../../../features/RouteSlice";
import DroneInfo from "./DroneInfo";
import CellSelectionBar from "../CellSelectionBar";
import {
  didMissionStop,
  isMissionStopRequestedLatched,
  showMissionEndToastIfNeeded,
} from "../../../utils/missionStoppedToast";
import { useDemoScenario } from "../../../demo/DemoScenarioContext";
import { useT } from "../../../i18n";
// import DroneStreamCard from "./DroneStreamCard"; // 지도 플로팅(StreamFloat)으로 대체

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return null;
  const total = Number(seconds);
  if (Number.isNaN(total)) return null;
  if (total < 0) return null;
  const h = String(Math.floor(total / 3600)).padStart(2, "0");
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(Math.floor(total % 60)).padStart(2, "0");
  return `${h}:${m}:${s}`;
};
// ETA = 미션 시작 시 1회 계산된 총 소요 시간(초). BE 가 드론별 완주 이력으로 추정하고(이력 없으면 4N+5),
// 미션 동안 고정 — 카운트다운 X. 남은 시간(time_left_sec)은 BE 가 eta − 비행시간으로 매 프레임 단조 감소시켜 준다.
const formatEta = (value) => formatDuration(value);

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeCount = (value, fallback = 0) => {
  const num = toNumber(value);
  if (num === null) return fallback;
  return Math.max(0, Math.round(num));
};

const normalizePercent = (value, fallback = null) => {
  const num = toNumber(value);
  if (num === null) return fallback;
  return Math.max(0, Math.min(100, Math.round(num)));
};

const normalizeVoltage = (value, fallback = null) => {
  const num = toNumber(value);
  if (num === null) return fallback;
  return Math.round(num * 100) / 100;
};

const hasKey = (obj, key) =>
  obj != null &&
  typeof obj === "object" &&
  Object.prototype.hasOwnProperty.call(obj, key);

// WS에서 도착한 키가 있으면 해당 값을 그대로 포맷(리셋 시 null→dash), 없으면 REST 스냅샷 유지
const pickLiveTiming = (droneData, key, formatter, restFallback) =>
  hasKey(droneData, key) ? formatter(droneData[key]) : restFallback;

// [1초 틱] 서버 값(초)에 프레임 이후 경과초를 얹는다/뺀다. null·음수(-1 = 미션 아님)는 그대로 통과.
const tickUp = (value, elapsedSec) => {
  const n = toNumber(value);
  return n == null || n < 0 ? value : n + elapsedSec;
};
const tickDown = (value, elapsedSec) => {
  const n = toNumber(value);
  return n == null || n < 0 ? value : Math.max(0, n - elapsedSec);
};

const isInfoDroneDisconnected = (droneInfo) =>
  droneInfo &&
  droneInfo.powerStatus == null &&
  droneInfo.gpsStatus == null &&
  (droneInfo.batteryCharge == null || droneInfo.batteryCharge === "");

// [탭 동시 로딩] 미션 API 는 droneId 기준이라 원래 info 응답을 기다려야 하는데,
// 직전 세션에서 확인된 farmId→droneId 를 캐시해 두면 새로고침 시 info 와 mission 을
// 병렬로 쏠 수 있다. 캐시가 틀렸을 때(드론 교체)를 대비해 응답은 info 가 확인해준
// droneId 와 일치할 때만 반영한다.
const missionDroneIdStorageKey = (farmId) => `tlatfarm:mission:droneId:${farmId}`;

const readCachedMissionDroneId = (farmId) => {
  if (!farmId || typeof window === "undefined") return null;
  try {
    const saved = window.sessionStorage.getItem(missionDroneIdStorageKey(farmId));
    const parsed = Number(saved);
    return saved && Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  } catch (error) {
    return null;
  }
};

const saveCachedMissionDroneId = (farmId, droneId) => {
  if (!farmId || typeof window === "undefined") return;
  try {
    if (droneId == null) {
      window.sessionStorage.removeItem(missionDroneIdStorageKey(farmId));
    } else {
      window.sessionStorage.setItem(
        missionDroneIdStorageKey(farmId),
        String(droneId)
      );
    }
  } catch (error) {
    // 캐시는 최적화일 뿐이므로 저장 실패는 무시 (기존 직렬 경로로 동작)
  }
};

export default function Mission() {
  const t = useT();
  const { isDemo, demoDroneOverview, demoInfo } = useDemoScenario();
  const farmId = useSelector((state) => state.farm.farmId);
  const droneId = useSelector((state) => state.drone.selectedDrone);
  const controls = useSelector((state) => state.drone.control);
  // 섹션이 바뀌면 예약 폼의 구역 목록도 다시 읽는다
  const [panelRefresh, setPanelRefresh] = useState(0);
  const missionReturning = useSelector((state) => state.drone.missionReturning);
  // 서버 하트비트 판정. 정보 탭·지도 배지와 같은 값이며, false 면 상태 칸을 "-" 로 가린다.
  const infoConnected = useSelector((state) => state.drone.infoConnected);
  const missionProgress = useSelector((state) => state.drone.missionProgress);
  const lastMissionResult = useSelector((state) => state.drone.lastMissionResult);

  // [1초 틱] 비행 시간/남은 시간이 WS 프레임(2초+) 사이에도 흐르게 매초 리렌더한다.
  // 예상 종료(eta)는 미션 시작 시 1회 계산된 고정값이라 틱을 얹지 않는다.
  const [, timingTick] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    if (!controls?.missionStatus) return undefined;
    const id = setInterval(timingTick, 1000);
    return () => clearInterval(id);
  }, [controls?.missionStatus]);
  const droneData = useSelector((state) => state.drone.droneData);
  const metricsAt = toNumber(droneData?.flight_metrics_at);
  const timingElapsedSec =
    controls?.missionStatus && metricsAt != null
      ? Math.max(0, Math.floor((Date.now() - metricsAt) / 1000))
      : 0;
  const controlsRef = useRef(controls);
  const selectedCellIds = useSelector((state) => state.route.selectedCellIds);
  const dispatch = useDispatch();

  const [drone, setDrone] = useState("");
  // 미션 데이터 로딩이 끝난 farmId. 로딩 중 부분 렌더링으로 화면이 튀는 것을 막는다.
  const [loadedFarmId, setLoadedFarmId] = useState(null);
  // 서버 하트비트 판정(infoConnected)의 마지막 dispatch 값 — 바뀔 때만 dispatch 하기 위한 가드
  const lastInfoConnectedRef = useRef(null);
  // 업로드 상태를 스냅샷에서 복원한 드론 id. 같은 드론에 두 번 복원하지 않는다.
  const uploadRestoredDroneRef = useRef(null);

  useEffect(() => {
    controlsRef.current = controls;
  }, [controls]);
  // 5초 폴링(/info)은 응답에 progress 가 없어, 마지막으로 받아 둔 값을 여기서 꺼내 토스트 분류에 넘긴다.
  const missionProgressRef = useRef(missionProgress);
  useEffect(() => {
    missionProgressRef.current = missionProgress;
  }, [missionProgress]);

  useEffect(() => {
    if (isDemo) return;
    if (!farmId) return;
    if (selectedCellIds.length > 0) return;
    if (missionReturning) return;
    if (isMissionStopRequestedLatched()) return;
    if (!controlsRef.current?.missionStatus) return;
    dispatch(fetchWaypoints({ farmId }));
  }, [farmId, dispatch, isDemo, missionReturning, selectedCellIds.length]);

  useEffect(() => {
    if (isDemo) {
      if (!farmId) {
        setLoadedFarmId(null);
        return undefined;
      }
      // 등록 드론이 없으면(demoInfo.drone === null) 실계정처럼 "드론 없음"으로 둔다.
      setDrone(demoInfo.drone ? demoDroneOverview : null);
      setLoadedFarmId(farmId);
      if (demoInfo.drone) dispatch(setSelectedDrone(demoInfo.drone.id));
      // infoConnected 는 여기서 정하지 않는다. 데모는 전원 상태를 따라가며,
      // 그 dispatch 는 DemoScenarioProvider 가 한 곳에서 맡는다.
      return undefined;
    }

    if (!farmId) {
      setDrone("");
      setLoadedFarmId(null);
      dispatch(setSelectedDrone(null));
      lastInfoConnectedRef.current = null;
      dispatch(setInfoConnected(null));
      return;
    }

    let cancelled = false;
    const fetchDroneData = async () => {
      try {
        // 캐시된 droneId 가 있으면 mission 을 info 와 병렬로 미리 요청한다.
        // (.catch 로 실패를 값으로 받아 unhandled rejection 방지 — 판정은 아래에서)
        const cachedDroneId = readCachedMissionDroneId(farmId);
        const infoPromise = axios.get(`/api/dashboard/${farmId}/info`);
        const cachedMissionPromise = cachedDroneId
          ? axios
              .get(`/api/dashboard/${cachedDroneId}/mission`)
              .catch((error) => error)
          : null;

        const infoRes = await infoPromise;
        if (cancelled) return;
        const infoDrone = infoRes.data?.data?.droneInfo ?? null;
        const resolvedDroneId = infoDrone?.id ?? null;
        dispatch(setSelectedDrone(resolvedDroneId));
        saveCachedMissionDroneId(farmId, resolvedDroneId);

        // 서버 하트비트 판정을 FlightNote 와 공유 (드론 없으면 null = 판정 없음).
        // 정보 탭과 동일하게 isConnected 필드가 1차 기준 — powerStatus=false 인
        // 드론은 마스킹 휴리스틱으로는 끊김 판정이 안 되기 때문.
        const nextConnected = infoDrone
          ? typeof infoDrone.isConnected === "boolean"
            ? infoDrone.isConnected
            : !isInfoDroneDisconnected(infoDrone)
          : null;
        if (lastInfoConnectedRef.current !== nextConnected) {
          lastInfoConnectedRef.current = nextConnected;
          dispatch(setInfoConnected(nextConnected));
        }

        if (!resolvedDroneId) {
          setDrone("");
          return;
        }

        // 병렬 응답은 캐시 id 가 검증된 id 와 일치할 때만 채택.
        // 불일치(드론 교체)면 폐기하고 기존처럼 검증된 id 로 재요청한다.
        let data = null;
        if (cachedMissionPromise && cachedDroneId === resolvedDroneId) {
          const cachedResult = await cachedMissionPromise;
          if (cancelled) return;
          if (!(cachedResult instanceof Error)) {
            data = cachedResult.data;
          } else if (cachedResult.response?.status === 404) {
            // 검증된 id 의 404 = 미션 없음 (기존 catch 의 404 처리와 동일)
            setDrone("");
            return;
          }
          // 404 외 실패는 아래에서 한 번 더 시도
        }

        if (!data) {
          ({ data } = await axios.get(
            `/api/dashboard/${resolvedDroneId}/mission`
          ));
          if (cancelled) return;
        }
        const missionData = data?.data;
        const overview = missionData?.overview;
        const waypointProgress = overview?.waypointProgress;
        const missionControls = missionData?.controls;
        const missionWaypoints = missionData?.waypoints?.items ?? [];

        if (!overview) {
          setDrone("");
          return;
        }

        const completed = normalizeCount(waypointProgress?.completed, 0);
        const total = normalizeCount(waypointProgress?.total, 0);
        const calculatedPercent =
          total > 0 ? Math.round((completed * 100) / total) : 0;

        const normalizedOverview = {
          droneName: overview?.droneName ?? "Drone",
          battery: {
            // BE가 stale일 때 null로 마스킹. fallback을 null로 두어 그대로 보존.
            percent: normalizePercent(overview?.battery?.percent, null),
            voltageV: normalizeVoltage(overview?.battery?.voltageV, null),
          },
          waypointProgress: {
            completed,
            total,
            percent: normalizePercent(
              waypointProgress?.percent,
              calculatedPercent
            ),
          },
          timing: {
            eta: formatEta(overview?.timing?.eta),
            flightTimeSec: formatDuration(overview?.timing?.flightTimeSec),
            timeLeftSec: formatDuration(overview?.timing?.timeLeftSec),
          },
        };

        setDrone(normalizedOverview);
        const prevControls = controlsRef.current ?? {};
        const rawNextMissionStatus =
          missionControls?.missionStatus ?? prevControls.missionStatus;
        // [stale-stop echo 가드] 수동정지 래치 활성 동안 BE가 (pre-commit 레이스로)
        // 돌려주는 mission_status=true는 stale 에코이므로 무시하고 옵티미스틱 false를
        // 유지한다. redux를 true로 되돌리지 않아(= 웨이포인트 깜박임 방지) 래치도 보존돼
        // 잘못된 "Mission has been stopped." 토스트를 막는다.
        const nextMissionStatus =
          isMissionStopRequestedLatched() &&
          rawNextMissionStatus === true &&
          prevControls.missionStatus === false
            ? prevControls.missionStatus
            : rawNextMissionStatus;
        dispatch(
          setControl({
            ...prevControls,
            powerStatus: missionControls?.powerStatus ?? prevControls.powerStatus,
            armingStatus:
              missionControls?.armingStatus ?? prevControls.armingStatus,
            missionStatus: nextMissionStatus,
            gpsStatus: missionControls?.gpsStatus ?? prevControls.gpsStatus,
            serialNum:
              missionControls?.serialNum ??
              missionControls?.port ??
              prevControls.serialNum,
          })
        );
        // 새로고침 복원: 드론이 마지막으로 보고한 경로 보관 상태(upload_status)로
        // "업로드 완료 / Start 가능" 을 되살린다. 보고가 없으면(null) 건드리지 않는다.
        // 드론당 한 번만. 이 효과는 셀 선택 수가 바뀔 때마다 다시 도는데, 그때마다 쓰면 사용자가
        // Cancel 로 비운 상태나 소켓이 방금 내린 값을 Redis 스냅샷의 옛 값으로 되돌린다.
        // 확인을 기다리는 중에 새로고침했으면 대기 표시도 이어서 낸다. 보고가 아직 없는 첫 업로드라도
        // 대기 중인 것은 맞으므로 접수 시각만 있으면 복원한다.
        const hasUploadReport = typeof missionControls?.uploadStatus === "boolean";
        if (
          (hasUploadReport || missionControls?.uploadPendingAt != null) &&
          uploadRestoredDroneRef.current !== resolvedDroneId
        ) {
          uploadRestoredDroneRef.current = resolvedDroneId;
          dispatch(
            setUploadStatus({
              status: hasUploadReport && missionControls.uploadStatus,
              routeId: hasUploadReport ? missionControls.uploadRouteId ?? null : null,
              pendingAt: missionControls.uploadPendingAt ?? null,
            })
          );
        }
        // 새로고침 복원: 드론이 마지막으로 보고한 수행 단계 라벨을 되살린다.
        if (typeof missionControls?.missionProgress === "string") {
          dispatch(setMissionProgress(missionControls.missionProgress));
          // 완주 직후 새로고침이면 진행 링도 N/N 으로 복원한다(경로 웨이포인트 수 = 전 구역).
          if (
            missionControls.missionProgress.trim().toLowerCase() === "complete" &&
            !missionControls?.missionStatus &&
            total > 0
          ) {
            dispatch(latchMissionResult({ total }));
          }
        }
        // 이 경로는 셀 선택이 바뀔 때마다 다시 돈다. 서버는 완주 프레임을 처리하며 셀을 먼저 비우므로
        // 이 재조회가 WS 프레임보다 앞서 종료 전환을 보는 일이 잦다. progress 를 같이 넘겨야
        // 메모(WS 만 적음)에 기대지 않고 완주를 바로 분류한다.
        showMissionEndToastIfNeeded(
          prevControls.missionStatus,
          nextMissionStatus,
          { progress: missionControls?.missionProgress }
        );
        // WS보다 이 REST 경로가 종료 전환을 먼저 관찰하는 경우에도
        // 오버레이가 같은 시점에 정리되도록 WS 경로와 동일하게 처리한다.
        if (didMissionStop(prevControls.missionStatus, nextMissionStatus)) {
          if (!isMissionStopRequestedLatched()) {
            dispatch(setMissionReturning(false));
          }
          dispatch(setWaypoints([]));
          // 선택은 서버가 미션 종료를 확정할 때 한 번만 비운다. 화면마다 보내면 늦게 도착한 지우기가 새 선택을 지운다.
          dispatch(setRouteConfirmed(false));
        }

        if (selectedCellIds.length === 0) {
          if (
            !missionReturning &&
            !isMissionStopRequestedLatched() &&
            missionControls?.missionStatus &&
            missionWaypoints.length
          ) {
            dispatch(setWaypoints(missionWaypoints));
          } else {
            dispatch(setWaypoints([]));
          }
        }
      } catch (error) {
        if (cancelled) return;
        const status = error?.response?.status;
        if (status === 404) {
          // Backend uses this endpoint with "droneId". If it's not found (or no drone assigned), treat as no mission.
          setDrone("");
          return;
        }
        console.error("Failed to load mission data: ", error);
      } finally {
        // 성공/실패/드론 없음 모두 로딩 완료로 처리해 무한 대기 상태를 막는다.
        if (!cancelled) setLoadedFarmId(farmId);
      }
    };

    fetchDroneData();
    return () => {
      cancelled = true;
    };
  }, [demoDroneOverview, demoInfo.drone, dispatch, farmId, isDemo, missionReturning, selectedCellIds.length]);

  useEffect(() => {
    if (isDemo) return undefined;
    if (!farmId || !droneId) return;

    let cancelled = false;

    const refreshDroneDisplayFromInfo = async () => {
      try {
        const { data } = await axios.get(`/api/dashboard/${farmId}/info`);
        if (cancelled) return;

        const infoDrone = data?.data?.droneInfo;
        if (!infoDrone) {
          // 농장에 드론이 없어진 경우 — 판정 없음으로 되돌림
          if (lastInfoConnectedRef.current !== null) {
            lastInfoConnectedRef.current = null;
            dispatch(setInfoConnected(null));
          }
          return;
        }
        if (infoDrone.id !== droneId) return;

        // 서버 하트비트 판정을 FlightNote 와 공유 — 값이 바뀔 때만 dispatch.
        // 정보 탭과 동일하게 isConnected 필드가 1차 기준 (위 초기 로드와 같은 이유)
        const nextConnected =
          typeof infoDrone.isConnected === "boolean"
            ? infoDrone.isConnected
            : !isInfoDroneDisconnected(infoDrone);
        if (lastInfoConnectedRef.current !== nextConnected) {
          lastInfoConnectedRef.current = nextConnected;
          dispatch(setInfoConnected(nextConnected));
        }

        const prevControls = controlsRef.current ?? {};
        const hasMissionStatus = typeof infoDrone.missionStatus === "boolean";
        const nextMissionStatus = hasMissionStatus
          ? infoDrone.missionStatus
          : prevControls.missionStatus;

        dispatch(
          setControl({
            ...prevControls,
            powerStatus: infoDrone.powerStatus ?? prevControls.powerStatus,
            missionStatus: nextMissionStatus,
            // ARM 상태도 함께 되돌린다. 이게 빠져 있으면 WS ARMED_STATUS 를 놓쳤을 때
            // 화면이 영영 ARMED 로 남아 미션 시작이 DRONE-007 로 막힌다.
            armingStatus: infoDrone.armingStatus ?? prevControls.armingStatus,
            gpsStatus: infoDrone.gpsStatus ?? prevControls.gpsStatus,
          })
        );

        showMissionEndToastIfNeeded(
          prevControls.missionStatus,
          nextMissionStatus,
          { progress: missionProgressRef.current }
        );

        if (didMissionStop(prevControls.missionStatus, nextMissionStatus)) {
          if (!isMissionStopRequestedLatched()) {
            dispatch(setMissionReturning(false));
          }
          dispatch(setWaypoints([]));
          // 선택은 서버가 미션 종료를 확정할 때 한 번만 비운다. 화면마다 보내면 늦게 도착한 지우기가 새 선택을 지운다.
          dispatch(setRouteConfirmed(false));
        }

        setDrone((prev) => {
          if (!prev) return prev;

          const nextBatteryPercent = isInfoDroneDisconnected(infoDrone)
            ? null
            : normalizePercent(infoDrone.batteryCharge, prev.battery?.percent ?? null);

          return {
            ...prev,
            droneName: infoDrone.droneName ?? prev.droneName,
            battery: {
              ...prev.battery,
              percent: nextBatteryPercent,
            },
          };
        });
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to refresh mission drone display: ", error);
      }
    };

    const interval = setInterval(refreshDroneDisplayFromInfo, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [farmId, droneId, dispatch, isDemo]);

  const missionActive = controls?.missionStatus === true;

  // [미션 아님] 진행 링 값.
  //  - 직전 완주 결과(래치)가 있으면 N/N · 100% 유지 — 착륙 즉시 0/0 으로 지워지던 문제.
  //  - 아니면 0/선택 셀 수. 업로드 전엔 선택한 셀 수가 곧 이번 미션의 전체 구역이다.
  const idleWaypointProgress = lastMissionResult
    ? {
        completed: lastMissionResult.done,
        total: lastMissionResult.total,
        percent: 100,
      }
    : {
        completed: 0,
        total: selectedCellIds.length,
        percent: 0,
      };

  const displayDrone =
    isDemo
      ? {
          ...demoDroneOverview,
          // 실계정과 같은 규칙으로 그린다: 미션 중이면 라이브 값, 아니면
          // 직전 완주 래치(N/N) 또는 0/선택 셀 수.
          // 예전에는 데모만 mission 객체를 그대로 보여 줘서, 4칸 미션을 끝낸 뒤
          // 5칸을 새로 골라 업로드해도 진행 링이 "4/4 100%" 로 남아 있었다.
          waypointProgress: missionActive
            ? demoDroneOverview.waypointProgress
            : idleWaypointProgress,
        }
      : drone
      ? {
          ...drone,
          battery: {
            ...drone.battery,
          },
          waypointProgress: missionActive
            ? {
                ...drone.waypointProgress,
                completed:
                  normalizeCount(
                    droneData?.waypoint_done,
                    drone.waypointProgress.completed ?? 0
                  ) ?? 0,
                total:
                  normalizeCount(
                    droneData?.waypoint_total,
                    drone.waypointProgress.total ?? 0
                  ) ?? 0,
                percent:
                  normalizePercent(
                    droneData?.waypoint_percent,
                    drone.waypointProgress.percent ?? 0
                  ) ?? 0,
              }
            : idleWaypointProgress,
          timing: {
            ...drone.timing,
            eta: pickLiveTiming(droneData, "eta", formatEta, drone.timing.eta ?? null),
            // [1초 틱] WS 프레임 사이에도 비행 시간은 +, 남은 시간은 − 로 흐른다.
            flightTimeSec: hasKey(droneData, "flight_time_sec")
              ? formatDuration(tickUp(droneData.flight_time_sec, timingElapsedSec))
              : drone.timing.flightTimeSec,
            timeLeftSec: hasKey(droneData, "time_left_sec")
              ? formatDuration(tickDown(droneData.time_left_sec, timingElapsedSec))
              : drone.timing.timeLeftSec,
          },
        }
      : drone;

  // 첫 로딩(새로고침/농장 전환) 중에는 스켈레톤으로 자리를 먼저 잡는다.
  // 빈 화면 → 한 번에 등장 대신, 뼈대는 즉시 보이고 값만 채워지는 체감을 준다.
  if (loadedFarmId !== farmId) {
    return (
      <div className="mission-panel" aria-busy="true">
        {/* 진행 링 자리 */}
        <div className="calm-skel-ring">
          <span className="calm-skel-circle" />
          <span className="calm-skel-lines">
            <i style={{ width: "58%" }} />
            <i style={{ width: "38%" }} />
          </span>
        </div>
        {/* 비행 상태 행 자리 */}
        {[78, 62, 70, 55, 66].map((w, i) => (
          <div className="calm-skel-row" key={i}>
            <i style={{ width: `${w}%` }} />
          </div>
        ))}
        {/* Drone Controls 자리 */}
        <div className="calm-skel-row" style={{ borderBottom: 0 }}>
          <i style={{ width: "34%" }} />
        </div>
        <div className="calm-skel-controls">
          <span className="calm-skel-power" />
          <span className="calm-skel-lines" style={{ maxWidth: 140 }}>
            <i style={{ width: "100%" }} />
            <i style={{ width: "80%" }} />
          </span>
        </div>
      </div>
    );
  }

  if (!droneId) return null;

  // 배터리 percent == null → 드론 오프라인(Offline 뱃지와 동일 기준). 이때만 조작을 잠근다.
  // 0 은 잠그지 않는다 — 실기체(c333, 2026-09-16)는 전원을 켠 뒤 1분 넘게 0 을 보내다가 실제 값(70)으로
  // 올라왔다. 측정 전 과도 구간인데 "전원을 켜세요" 로 잠겨 사용자가 전원을 반복해 눌렀다.
  // 방전이라도 Start 는 60% 게이트가 막고, 업로드는 땅에서 하는 일이라 0 잠금이 막아 주던 위험은 없다.
  const batteryPercent = displayDrone?.battery?.percent ?? null;
  const progressLabel = missionProgressLabel(missionProgress);
  const powerDisabled = batteryPercent == null;

  // 진행 링 게이지 (실데이터: overview.waypointProgress)
  const wp = displayDrone?.waypointProgress ?? {};
  const wpDone = Number(wp.completed) || 0;
  const wpTotal = Number(wp.total) || 0;

  return (
    <div className="mission-panel">
      <div className="calm-ring">
        <div className="txt">
          <div className="drone-name">{displayDrone?.droneName || "-"}</div>
          <div className="d">
            {/* 드론이 끊기면 마지막 단계("대기")가 남지 않게 오프라인으로 바꾼다. 상태 칸과 같은 판정.
                데모도 같은 판정을 탄다 — 데모는 전원이 곧 연결이라 전원을 끄면 여기로 들어온다. */}
            {infoConnected === false
              ? t("common.flightNote.offline")
              : progressLabel ||
                (controls?.missionStatus
                  ? t("mission.status.flying")
                  : controls?.powerStatus
                    ? t("mission.status.poweredIdle")
                    : t("mission.status.powerOff"))}
          </div>
        </div>
        {/* 모바일에서만 보인다(CalmTheme.css). 지도 위 셀 선택 바를 이 빈 공간으로 옮긴 것. */}
        <CellSelectionBar variant="inline" />
      </div>

      {displayDrone && (
        <DroneInfo
          drone={displayDrone}
          gpsStatus={controls?.gpsStatus}
          progress={{ done: wpDone, total: wpTotal }}
          offline={infoConnected === false}
        />
      )}
      <DroneDetailPanelControl
        powerDisabled={powerDisabled}
        batteryPercent={batteryPercent}
        sectionsRefresh={panelRefresh}
      />
      <SectionPanel onChanged={() => setPanelRefresh((n) => n + 1)} />
      <SchedulerPanel refreshKey={panelRefresh} />
      {/* <DroneStreamCard /> — 지도 플로팅(StreamFloat)으로 대체 */}
    </div>
  );
}
